import { search, searchFacetValues } from '~/providers/products/products';
import { redirect } from '@remix-run/server-runtime';

import { paginationValidationSchema } from '~/utils/pagination';
import { LoaderFunctionArgs } from '@remix-run/router';

/**
 * This loader deals with loading product searches, which is used in both the search page and the
 * category list page.
 */
export function filteredSearchLoaderFromPagination(
  allowedPaginationLimits: Set<number>,
  paginationLimitMinimumDefault: number,
) {
  const searchPaginationSchema = paginationValidationSchema(
    allowedPaginationLimits,
  );

  return {
    validator: searchPaginationSchema,
    filteredSearchLoader: async ({ params, request }: LoaderFunctionArgs) => {
      const url = new URL(request.url);
      const term = url.searchParams.get('q');
      const facetValueIds = url.searchParams.getAll('fvid');
      const limit =
        url.searchParams.get('limit') ?? paginationLimitMinimumDefault;
      const page = url.searchParams.get('page') ?? 1;

      const discountFvid = url.searchParams.get('discount_fvid');
      const bulkFvid = url.searchParams.get('bulk_fvid');
      const sortParam = url.searchParams.get('sort');

      // Construct Facet Filters for AND logic
      // (Origin OR Origin) AND (Discount) AND (Bulk)
      const facetValueFilters: any[] = [];
      if (facetValueIds.length) {
        facetValueFilters.push({ or: facetValueIds });
      }
      if (discountFvid) {
        facetValueFilters.push({ or: [discountFvid] });
      }
      if (bulkFvid) {
        facetValueFilters.push({ or: [bulkFvid] });
      }

      let sort: any = {};
      if (sortParam === 'price') sort = { price: 'ASC' };
      if (sortParam === '-price') sort = { price: 'DESC' };

      const zodResult = searchPaginationSchema.safeParse({ limit, page });
      if (!zodResult.success) {
        url.search = '';
        throw redirect(url.href);
      }

      let resultPromises: [
        ReturnType<typeof search>,
        ReturnType<typeof searchFacetValues>,
      ];
      const searchResultPromise = search(
        {
          input: {
            groupByProduct: true,
            term,
            facetValueFilters,
            collectionSlug: params.slug,
            take: zodResult.data.limit,
            skip: (zodResult.data.page - 1) * zodResult.data.limit,
            sort,
          } as any,
        },
        { request },
      );
      if (facetValueIds.length) {
        resultPromises = [
          searchResultPromise,
          searchFacetValues(
            {
              input: {
                groupByProduct: true,
                term,
                collectionSlug: params.slug,
              },
            },
            { request },
          ),
        ];
      } else {
        resultPromises = [searchResultPromise, searchResultPromise];
      }
      const [result, resultWithoutFacetValueFilters] = await Promise.all(
        resultPromises,
      );
      return {
        term,
        facetValueIds,
        result: result.search,
        resultWithoutFacetValueFilters: resultWithoutFacetValueFilters.search,
        appliedPaginationLimit: zodResult.data.limit,
        appliedPaginationPage: zodResult.data.page,
      };
    },
  };
}
